// src/funciones/gruposServicio.js

/*
==========================================================
YUTAKE

Servicio de grupos

Toda la lógica de grupos, miembros e invitaciones vive aquí
para que profesor y estudiante usen exactamente las mismas
reglas. Las pantallas solo llaman a estas funciones.

Roles:
  - Administrador: grupos.profesorPrincipal
  - Profesor:      grupos.profesores (incluye al admin)
  - Estudiante:    grupoMiembros con rol "estudiante"

Estados de grupoMiembros:
  pendiente | activo | rechazado | retirado
  (nunca se borra: se conserva el histórico)

Estados de grupos:
  activo | archivado
==========================================================
*/

import {
    addDoc,
    arrayRemove,
    arrayUnion,
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
    writeBatch
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";

/*----------------------------------------------------------
UTILIDADES
----------------------------------------------------------*/

// Sin letras ni números que se confundan (0/O, 1/I)
const ALFABETO = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

const generarCodigo = (largo = 6) => {
    const valores = new Uint32Array(largo);
    crypto.getRandomValues(valores);
    return Array.from(valores, v => ALFABETO[v % ALFABETO.length]).join("");
};

export const normalizarCodigo = (codigo) =>
    String(codigo || "").trim().toUpperCase();

export const enlaceInvitacion = (codigo) =>
    `${window.location.origin}/unirse/${codigo}`;

export const obtenerNombreUsuario = async (uid) => {
    try {
        const snap = await getDoc(doc(db, "usuarios", uid));
        if (snap.exists()) {
            const u = snap.data();
            return u.nick || u.nombre || "Usuario";
        }
    } catch (e) {
        // Sin permiso o sin conexión: se usa el genérico
    }
    return "Usuario";
};

/*----------------------------------------------------------
INVITACIÓN PENDIENTE (para volver a ella tras crear cuenta)
----------------------------------------------------------*/

const CLAVE_PENDIENTE = "yutake_invitacion_pendiente";

export const guardarInvitacionPendiente = (codigo) => {
    try { localStorage.setItem(CLAVE_PENDIENTE, normalizarCodigo(codigo)); } catch (e) { /* sin almacenamiento */ }
};

export const leerInvitacionPendiente = () => {
    try { return localStorage.getItem(CLAVE_PENDIENTE) || ""; } catch (e) { return ""; }
};

export const limpiarInvitacionPendiente = () => {
    try { localStorage.removeItem(CLAVE_PENDIENTE); } catch (e) { /* sin almacenamiento */ }
};

/*----------------------------------------------------------
INVITACIONES
----------------------------------------------------------*/

const codigoDisponible = async () => {
    for (let intento = 0; intento < 6; intento++) {
        const codigo = generarCodigo();
        const snap = await getDoc(doc(db, "invitaciones", codigo));
        if (!snap.exists()) return codigo;
    }
    throw new Error("No fue posible generar un código único.");
};

const crearInvitacion = async ({ grupo, rol, uid, profesorNombre, codigo = null }) => {
    const codigoFinal = codigo || await codigoDisponible();

    await setDoc(doc(db, "invitaciones", codigoFinal), {
        grupoId: grupo.id,
        grupoNombre: grupo.nombre || "",
        profesorNombre: profesorNombre || "",
        rol,
        activa: true,
        creadaPor: uid,
        fechaCreacion: serverTimestamp()
    });

    return codigoFinal;
};

/*
Devuelve { estudiante: codigo, profesor: codigo }.
Si el grupo es antiguo y no tiene invitaciones, las crea
(reutilizando su codigoInvitacion para estudiantes si es posible).
*/
export const asegurarInvitaciones = async (grupo, uid) => {
    const snap = await getDocs(
        query(
            collection(db, "invitaciones"),
            where("grupoId", "==", grupo.id),
            where("activa", "==", true)
        )
    );

    const codigos = {};

    snap.docs.forEach(d => {
        const rol = d.data().rol;
        if (!codigos[rol]) codigos[rol] = d.id;
    });

    if (codigos.estudiante && codigos.profesor) {
        return codigos;
    }

    const profesorNombre = await obtenerNombreUsuario(grupo.profesorPrincipal);

    for (const rol of ["estudiante", "profesor"]) {
        if (codigos[rol]) continue;

        let reutilizable = null;

        if (rol === "estudiante" && grupo.codigoInvitacion) {
            const antiguo = normalizarCodigo(grupo.codigoInvitacion);
            const existe = await getDoc(doc(db, "invitaciones", antiguo));
            if (!existe.exists()) reutilizable = antiguo;
        }

        codigos[rol] = await crearInvitacion({
            grupo, rol, uid, profesorNombre, codigo: reutilizable
        });
    }

    return codigos;
};

// Solo admin: desactiva el código actual y crea uno nuevo
export const renovarInvitacion = async (grupo, rol, codigoActual, uid) => {
    if (codigoActual) {
        await updateDoc(doc(db, "invitaciones", codigoActual), { activa: false });
    }
    const profesorNombre = await obtenerNombreUsuario(grupo.profesorPrincipal);
    return crearInvitacion({ grupo, rol, uid, profesorNombre });
};

export const obtenerInvitacion = async (codigo) => {
    const c = normalizarCodigo(codigo);
    if (!c) return null;
    const snap = await getDoc(doc(db, "invitaciones", c));
    return snap.exists() ? { codigo: c, ...snap.data() } : null;
};

/*----------------------------------------------------------
GRUPOS (profesor)
----------------------------------------------------------*/

export const crearGrupo = async ({ nombre, descripcion = "", examenes = [] }, uid) => {
    if (!nombre?.trim()) throw new Error("El grupo necesita un nombre.");

    const profesorNombre = await obtenerNombreUsuario(uid);

    const ref = await addDoc(collection(db, "grupos"), {
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
        examenes,
        profesorPrincipal: uid,
        profesores: [uid],
        estado: "activo",
        fechaCreacion: serverTimestamp()
    });

    const grupo = { id: ref.id, nombre: nombre.trim() };

    await setDoc(doc(db, "grupoMiembros", `${ref.id}_${uid}`), {
        grupoId: ref.id,
        usuarioId: uid,
        rol: "profesor",
        estado: "activo",
        fechaIngreso: serverTimestamp()
    });

    await crearInvitacion({ grupo, rol: "estudiante", uid, profesorNombre });
    await crearInvitacion({ grupo, rol: "profesor", uid, profesorNombre });

    return ref.id;
};

// Grupos donde el usuario es profesor (admin o no), sin archivados
export const cargarGruposProfesor = async (uid) => {
    const snap = await getDocs(
        query(
            collection(db, "grupos"),
            where("profesores", "array-contains", uid)
        )
    );

    return snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(g => g.estado !== "archivado")
        .map(g => ({ ...g, esAdmin: g.profesorPrincipal === uid }))
        .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
};

// Conteos rápidos para las tarjetas (sin cargar nombres)
export const resumenMiembros = async (grupoId) => {
    const snap = await getDocs(
        query(collection(db, "grupoMiembros"), where("grupoId", "==", grupoId))
    );

    const miembros = snap.docs.map(d => d.data());

    return {
        estudiantes: miembros.filter(m => m.estado === "activo" && m.rol === "estudiante").length,
        pendientes: miembros.filter(m => m.estado === "pendiente").length
    };
};

// Detalle completo para el panel de administración
export const cargarMiembros = async (grupo) => {
    const snap = await getDocs(
        query(collection(db, "grupoMiembros"), where("grupoId", "==", grupo.id))
    );

    const miembros = await Promise.all(
        snap.docs.map(async d => {
            const m = { docId: d.id, ...d.data() };
            m.nombre = await obtenerNombreUsuario(m.usuarioId);
            return m;
        })
    );

    // Los profesores salen de grupos.profesores (fuente de verdad),
    // enlazando su membresía si existe (grupos antiguos no la tienen).
    const profesores = await Promise.all(
        (grupo.profesores || []).map(async uid => {
            const membresia = miembros.find(m => m.usuarioId === uid && m.rol === "profesor");
            return {
                usuarioId: uid,
                docId: membresia?.docId || null,
                rol: "profesor",
                nombre: membresia?.nombre || await obtenerNombreUsuario(uid),
                esAdmin: uid === grupo.profesorPrincipal
            };
        })
    );

    return {
        pendientes: miembros.filter(m => m.estado === "pendiente"),
        estudiantes: miembros
            .filter(m => m.estado === "activo" && m.rol === "estudiante")
            .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre))),
        profesores: profesores.sort((a, b) => Number(b.esAdmin) - Number(a.esAdmin))
    };
};

/*----------------------------------------------------------
ACCIONES DEL ADMINISTRADOR
----------------------------------------------------------*/

export const aprobarSolicitud = async (grupo, miembro, uidAdmin) => {
    const batch = writeBatch(db);

    batch.update(doc(db, "grupoMiembros", miembro.docId), {
        estado: "activo",
        fechaIngreso: serverTimestamp(),
        aprobadoPor: uidAdmin
    });

    if (miembro.rol === "profesor") {
        batch.update(doc(db, "grupos", grupo.id), {
            profesores: arrayUnion(miembro.usuarioId)
        });
    }

    await batch.commit();
};

export const rechazarSolicitud = async (miembro) => {
    await updateDoc(doc(db, "grupoMiembros", miembro.docId), {
        estado: "rechazado",
        fechaRespuesta: serverTimestamp()
    });
};

export const retirarMiembro = async (grupo, miembro) => {
    if (miembro.usuarioId === grupo.profesorPrincipal) {
        throw new Error("El administrador no puede retirarse. Primero cede la administración.");
    }

    const batch = writeBatch(db);

    if (miembro.docId) {
        batch.update(doc(db, "grupoMiembros", miembro.docId), {
            estado: "retirado",
            fechaRetiro: serverTimestamp()
        });
    }

    if (miembro.rol === "profesor") {
        batch.update(doc(db, "grupos", grupo.id), {
            profesores: arrayRemove(miembro.usuarioId)
        });
    }

    await batch.commit();
};

// El admin actual pasa a ser profesor normal
export const cederAdministracion = async (grupo, nuevoAdminUid) => {
    if (!(grupo.profesores || []).includes(nuevoAdminUid)) {
        throw new Error("Solo un profesor del grupo puede ser administrador.");
    }
    await updateDoc(doc(db, "grupos", grupo.id), {
        profesorPrincipal: nuevoAdminUid
    });
};

// Oculta el grupo y desactiva sus enlaces. No borra nada.
export const archivarGrupo = async (grupo) => {
    const invitacionesSnap = await getDocs(
        query(
            collection(db, "invitaciones"),
            where("grupoId", "==", grupo.id),
            where("activa", "==", true)
        )
    );

    const batch = writeBatch(db);

    invitacionesSnap.docs.forEach(d => batch.update(d.ref, { activa: false }));

    batch.update(doc(db, "grupos", grupo.id), {
        estado: "archivado",
        fechaArchivado: serverTimestamp()
    });

    await batch.commit();
};

/*----------------------------------------------------------
ESTUDIANTE / INVITADO
----------------------------------------------------------*/

export const buscarMembresia = async (grupoId, uid) => {
    const snap = await getDocs(
        query(
            collection(db, "grupoMiembros"),
            where("grupoId", "==", grupoId),
            where("usuarioId", "==", uid)
        )
    );
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { docId: d.id, ...d.data() };
};

export const solicitarIngreso = async (invitacion, uid) => {
    const existente = await buscarMembresia(invitacion.grupoId, uid);

    if (existente && (existente.estado === "activo" || existente.estado === "pendiente")) {
        return { estado: existente.estado, rol: existente.rol, yaExistia: true };
    }

    const datos = {
        estado: "pendiente",
        rol: invitacion.rol,
        codigo: invitacion.codigo,
        fechaSolicitud: serverTimestamp()
    };

    if (existente) {
        await updateDoc(doc(db, "grupoMiembros", existente.docId), datos);
    } else {
        await setDoc(doc(db, "grupoMiembros", `${invitacion.grupoId}_${uid}`), {
            grupoId: invitacion.grupoId,
            usuarioId: uid,
            ...datos
        });
    }

    return { estado: "pendiente", rol: invitacion.rol, yaExistia: false };
};

// Grupos del estudiante: solo nombre, descripción y profesores
export const cargarGruposEstudiante = async (uid) => {
    const snap = await getDocs(
        query(collection(db, "grupoMiembros"), where("usuarioId", "==", uid))
    );

    const membresias = snap.docs
        .map(d => ({ docId: d.id, ...d.data() }))
        .filter(m =>
            m.rol === "estudiante" &&
            (m.estado === "activo" || m.estado === "pendiente")
        );

    const grupos = await Promise.all(
        membresias.map(async m => {
            const g = await getDoc(doc(db, "grupos", m.grupoId));
            if (!g.exists()) return null;

            const datos = g.data();
            if (datos.estado === "archivado") return null;

            const profesores = await Promise.all(
                (datos.profesores || []).map(obtenerNombreUsuario)
            );

            return {
                id: g.id,
                nombre: datos.nombre,
                descripcion: datos.descripcion,
                examenes: datos.examenes || [],
                profesores,
                membresia: m
            };
        })
    );

    return grupos.filter(Boolean);
};

export const abandonarGrupo = async (docIdMembresia) => {
    await updateDoc(doc(db, "grupoMiembros", docIdMembresia), {
        estado: "retirado",
        fechaRetiro: serverTimestamp()
    });
};